import { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Images, Briefcase, FileText, MessageSquare, Users, Calendar,
  TrendingUp, Mail, ArrowRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { adminFetch } from '../../utils/adminFetch';
import { CloudinaryUsageWidget } from '../../components/CloudinaryUsageWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { stats } = useOutletContext() || {};
  const [recentMessages, setRecentMessages] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const [messagesRes, eventsRes] = await Promise.all([
          adminFetch(`${API}/admin/messages`),
          adminFetch(`${API}/admin/calendar`)
        ]);
        
        if (messagesRes.ok) {
          const messages = await messagesRes.json();
          setRecentMessages(messages.slice(0, 5));
        }
        if (eventsRes.ok) {
          const events = await eventsRes.json();
          const upcoming = events
            .filter(e => e.date >= new Date().toISOString().split('T')[0])
            .slice(0, 5);
          setUpcomingEvents(upcoming);
        }
      } catch (error) {
        console.error('Error fetching recent data:', error);
      }
    };
    fetchRecent();
  }, []);

  const statCards = [
    { icon: Images, label: 'Galerías', value: stats?.galleries || 0, path: '/admin/galleries', color: 'bg-blue-500' },
    { icon: Briefcase, label: 'Servicios', value: stats?.services || 0, path: '/admin/services', color: 'bg-green-500' },
    { icon: FileText, label: 'Posts', value: stats?.blog_posts || 0, path: '/admin/blog', color: 'bg-purple-500' },
    { icon: MessageSquare, label: 'Testimonios', value: stats?.testimonials || 0, path: '/admin/testimonials', color: 'bg-amber-500' },
    { icon: Users, label: 'Clientes', value: stats?.clients || 0, path: '/admin/clients', color: 'bg-rose-500' },
    { icon: Calendar, label: 'Próximos eventos', value: stats?.events_upcoming || 0, path: '/admin/calendar', color: 'bg-cyan-500' },
  ];

  return (
    <div className="p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="font-serif text-3xl lg:text-4xl mb-2">Dashboard</h1>
        <p className="text-muted-foreground mb-8">Resumen general de tu web</p>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link 
                to={stat.path}
                className="block bg-card border border-border p-4 hover:border-foreground/20 transition-colors"
              >
                <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <p className="font-serif text-2xl mb-1">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-card border border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm font-medium">Newsletter</span>
            </div>
            <p className="font-serif text-3xl mb-1">{stats?.newsletter_subscribers || 0}</p>
            <p className="text-xs text-muted-foreground">suscriptores activos</p>
          </div>
          <div className="bg-card border border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <MessageSquare className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm font-medium">Mensajes</span>
            </div>
            <p className="font-serif text-3xl mb-1">{stats?.contact_messages || 0}</p>
            <p className="text-xs text-muted-foreground">mensajes de contacto</p>
          </div>
          <div className="bg-card border border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm font-medium">Clientes potenciales</span>
            </div>
            <p className="font-serif text-3xl mb-1">{stats?.clients_potential || 0}</p>
            <p className="text-xs text-muted-foreground">pendientes de seguimiento</p>
          </div>
        </div>

        {/* Cloudinary Usage */}
        <div className="mb-8">
          <CloudinaryUsageWidget />
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Messages */}
          <div className="bg-card border border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-medium">Mensajes recientes</h2>
              <Link to="/admin/clients" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                Ver todos <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="divide-y divide-border">
              {recentMessages.length > 0 ? (
                recentMessages.map((msg) => (
                  <div key={msg.message_id} className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{msg.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{msg.subject}</p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(msg.created_at).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  No hay mensajes recientes
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="bg-card border border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-medium">Próximos eventos</h2>
              <Link to="/admin/calendar" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                Ver calendario <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="divide-y divide-border">
              {upcomingEvents.length > 0 ? (
                upcomingEvents.map((event) => (
                  <div key={event.event_id} className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{event.title}</p>
                        <p className="text-sm text-muted-foreground">{event.service_type}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{event.date}</p>
                        <p className="text-xs text-muted-foreground">{event.time}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  No hay eventos próximos
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
